import { useState } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { Download } from 'lucide-react';
import { downloadYunExpressExcel, getYunOrderCount } from '../utils/yunExpressExport';
import { useOMS } from '../../../../apps/hooks/oms/useOMS';

interface YunExportModalProps {
  readonly show: boolean;
  readonly onHide: () => void;
}

function getTodayString(): string {
  const today = new Date();
  return today.toISOString().split('T')[0] ?? '';
}

export function YunExportModal({ show, onHide }: YunExportModalProps) {
  const today = getTodayString();
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [isLoading, setIsLoading] = useState(false);
  const [yunCount, setYunCount] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const oms = useOMS();

  const handleDownload = async () => {
    if (!startDate || !endDate) return;

    setIsLoading(true);
    setError(null);
    try {
      const orders = await oms.listOrdersByDateRange(startDate, endDate);
      const count = getYunOrderCount(orders);
      setYunCount(count);
      if (count > 0) {
        downloadYunExpressExcel(orders, startDate, endDate);
      }
      onHide();
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Failed to fetch orders. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleShow = () => {
    setStartDate(today);
    setEndDate(today);
    setYunCount(null);
    setError(null);
  };

  return (
    <Modal show={show} onHide={onHide} onShow={handleShow} centered>
      <Modal.Header closeButton>
        <Modal.Title>Yun Express Export</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <div className="d-flex gap-3 mb-3">
            <Form.Group className="flex-1">
              <Form.Label>Start Date</Form.Label>
              <Form.Control
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                disabled={isLoading}
              />
            </Form.Group>
            <Form.Group className="flex-1">
              <Form.Label>End Date</Form.Label>
              <Form.Control
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={isLoading}
              />
            </Form.Group>
          </div>
          <div className="p-3 bg-light rounded">
            <p className="mb-0 small text-muted">
              Exports prepare-shipment YUN_EXPRESS orders only in Yun Express standard template format.
            </p>
          </div>
          {yunCount !== null && yunCount === 0 && (
            <div className="mt-2 p-2 bg-warning bg-opacity-10 rounded">
              <p className="mb-0 small text-warning">No prepare-shipment YUN orders found in the selected date range.</p>
            </div>
          )}
          {error && (
            <div className="mt-2 p-2 bg-danger bg-opacity-10 rounded">
              <p className="mb-0 small text-danger">{error}</p>
            </div>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="success"
          onClick={handleDownload}
          disabled={isLoading || !startDate || !endDate}
          className="d-flex align-items-center gap-2"
        >
          {isLoading ? (
            <>
              <Spinner size="sm" />
              Exporting...
            </>
          ) : (
            <>
              <Download size={16} />
              Download Excel
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
